import { css, type CSSObject } from '@emotion/react'
import Box from '@mui/material/Box'
import { noop } from 'lodash'
import React, { type MutableRefObject, type ReactNode, useEffect } from 'react'
// eslint-disable-next-line import/no-unresolved -- typed via src/types/swiper-element-bundle.d.ts; eslint's resolver can't read swiper's exports map
import { register } from 'swiper/element/bundle'

import type { SxProps, Theme } from '@mui/material/styles'
import type { Swiper } from 'swiper'

/** The `<swiper-container>` custom element after `register()` upgrades it. */
export type SwiperElement = HTMLElement & {
  swiper: Swiper
  initialize: () => void
  initialized?: boolean
}

type SwiperBreakpoint = { slidesPerView?: number | 'auto'; spaceBetween?: number }

type SwiperContainerProps = {
  breakpoints?: Record<string, SwiperBreakpoint>
  bulletActiveClass?: string
  centeredSlides?: boolean
  children?: ReactNode
  className?: string
  clickable?: boolean
  freeMode?: boolean
  loop?: boolean
  mousewheel?: boolean
  // Built-in prev/next arrows (styled via --swiper-navigation-* CSS vars in sx).
  navigation?: boolean
  // Watches slide mutations so async content (e.g. images resolved after init) re-measures
  // the swiper instead of rendering collapsed/stacked slides.
  observer?: boolean
  slidesPerView?: number | 'auto'
  spaceBetween?: number
  swiperRef: MutableRefObject<SwiperElement | null>
  sx?: SxProps<Theme>
  thumbsSwiper?: Swiper | null
  // CSS selector of the thumbs swiper element — resolves at init, so it works even
  // when both swipers mount in the same render (an instance ref would still be null).
  thumbsSwiperSelector?: string
  watchSlidesProgress?: boolean
  onReachEnd?: (swiper: Swiper) => void
  onSlideChange?: (swiper: Swiper) => void
}

const SwiperContainer = ({
  breakpoints,
  bulletActiveClass = 'swiper-pagination-bullet-active',
  centeredSlides = false,
  children,
  className,
  clickable = true,
  freeMode = true,
  loop = false,
  mousewheel = false,
  navigation = false,
  observer = false,
  slidesPerView = 1,
  spaceBetween = 0,
  swiperRef,
  sx,
  thumbsSwiper,
  thumbsSwiperSelector,
  watchSlidesProgress = false,
  onReachEnd = noop,
  onSlideChange = noop,
}: SwiperContainerProps) => {
  useEffect(() => {
    register()

    // sx is a plain style object at runtime; emotion serializes it to inject into the swiper
    // shadow DOM. MUI types it as SxProps, which is not assignable to emotion's CSSObject.
    const styles = sx ? css(sx as CSSObject).styles : ''
    const params = {
      breakpoints,
      centeredSlides,
      freeMode,
      // forceToAxis keeps a vertical mouse-wheel scrolling the page, not the strip.
      mousewheel: mousewheel ? { forceToAxis: true } : false,
      injectStyles: sx ? [styles] : [],
      loop,
      navigation,
      observer,
      pagination: {
        clickable,
        bulletActiveClass,
      },
      slidesPerView,
      spaceBetween,
      thumbs: {
        swiper: thumbsSwiper ?? thumbsSwiperSelector ?? null,
      },
      watchSlidesProgress,
      on: {
        reachEnd: onReachEnd,
        slideChange: onSlideChange,
      },
    }

    const swiperEl = swiperRef.current
    if (swiperEl && !swiperEl.initialized) {
      Object.assign(swiperEl, params)
      swiperEl.initialize()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- swiper initializes once on mount
  }, [])

  return (
    <Box sx={sx}>
      <swiper-container ref={swiperRef} className={className} init="false">
        {children}
      </swiper-container>
    </Box>
  )
}

export default SwiperContainer
